import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Drawer,
    FormControlLabel,
    IconButton,
    InputAdornment,
    Radio,
    RadioGroup,
    TextField,
    Typography,
} from '@material-ui/core'
import CallReceivedIcon from '@material-ui/icons/CallReceived'
import CloseIcon from '@material-ui/icons/Close'
import FilterListIcon from '@material-ui/icons/FilterList'
import PhoneIcon from '@material-ui/icons/Phone'
import SearchIcon from '@material-ui/icons/Search'
import StarsIcon from '@material-ui/icons/Stars'
import clsx from 'clsx'

import {
    CallListFilters,
    useVoiceAgentCallList,
} from '../../../hooks/useVoiceAgentCalls'
import { VoiceAgentCallCategory } from '../../../models/VoiceAgent'
import { usePhoneStyles } from '../../Phone/usePhoneStyles'
import { OutcomeChip } from '../../Phone/components/OutcomeChip'
import { useVoiceAgentStyles } from '../useVoiceAgentStyles'
import {
    CALL_CATEGORY_META,
    CALL_CATEGORY_TABS,
    formatE164,
    formatRelativeTime,
} from '../constants'
import { CallDetailPanel } from './CallDetailPanel'

interface CallLogPanelProps {
    selectedCallId?: string
    onSelectCall: (id: string) => void
    onDeselectCall: () => void
}

const toISODate = (date: string): string => {
    if (!date) return ''
    return new Date(date + 'T00:00:00').toISOString()
}

const toISODateEnd = (date: string): string => {
    if (!date) return ''
    return new Date(date + 'T23:59:59').toISOString()
}

export const CallLogPanel: React.FC<CallLogPanelProps> = ({
    selectedCallId,
    onSelectCall,
    onDeselectCall,
}) => {
    const phone = usePhoneStyles()
    const classes = useVoiceAgentStyles()
    const [page, setPage] = useState(1)
    const [drawerOpen, setDrawerOpen] = useState(false)

    const [categoryTab, setCategoryTab] = useState<
        VoiceAgentCallCategory | 'ALL'
    >('ALL')
    const [search, setSearch] = useState('')
    const [dateFrom, setDateFrom] = useState('')
    const [dateTo, setDateTo] = useState('')
    const [hasTranscript, setHasTranscript] = useState('')
    const [hasRecording, setHasRecording] = useState('')
    const [minDuration, setMinDuration] = useState('')
    const [maxDuration, setMaxDuration] = useState('')
    const [callTopic, setCallTopic] = useState('')

    const filters = useMemo<CallListFilters>(() => {
        const f: CallListFilters = {}
        if (search.trim()) f.search = search.trim()
        if (dateFrom) f.date_from = toISODate(dateFrom)
        if (dateTo) f.date_to = toISODateEnd(dateTo)
        if (hasTranscript === 'true') f.has_transcript = true
        if (hasTranscript === 'false') f.has_transcript = false
        if (hasRecording === 'true') f.has_recording = true
        if (hasRecording === 'false') f.has_recording = false
        if (minDuration && Number(minDuration) > 0)
            f.min_duration = Number(minDuration)
        if (maxDuration && Number(maxDuration) > 0)
            f.max_duration = Number(maxDuration)
        if (callTopic) f.call_topic = callTopic
        if (categoryTab !== 'ALL') f.call_category = categoryTab
        return f
    }, [
        search,
        dateFrom,
        dateTo,
        hasTranscript,
        hasRecording,
        minDuration,
        maxDuration,
        callTopic,
        categoryTab,
    ])

    const activeFilterCount = useMemo(() => {
        let n = 0
        if (dateFrom) n++
        if (dateTo) n++
        if (hasTranscript) n++
        if (hasRecording) n++
        if (minDuration) n++
        if (maxDuration) n++
        if (callTopic) n++
        return n
    }, [
        dateFrom,
        dateTo,
        hasTranscript,
        hasRecording,
        minDuration,
        maxDuration,
        callTopic,
    ])

    useEffect(() => {
        setPage(1)
    }, [filters])

    const {
        calls,
        count,
        hasNext,
        hasPrevious,
        isLoading,
        error,
        refetch,
    } = useVoiceAgentCallList(page, 25, filters)

    const dismissedRef = useRef(false)

    useEffect(() => {
        if (selectedCallId) {
            dismissedRef.current = false
            return
        }
        if (dismissedRef.current || calls.length === 0) return
        onSelectCall(calls[0].id)
    }, [calls, selectedCallId, onSelectCall])

    const handleDeselect = useCallback(() => {
        dismissedRef.current = true
        onDeselectCall()
    }, [onDeselectCall])

    const clearFilters = useCallback(() => {
        setDateFrom('')
        setDateTo('')
        setHasTranscript('')
        setHasRecording('')
        setMinDuration('')
        setMaxDuration('')
        setCallTopic('')
    }, [])

    if (isLoading && calls.length === 0 && !search && activeFilterCount === 0) {
        return (
            <Box display="flex" justifyContent="center" py={8}>
                <CircularProgress />
            </Box>
        )
    }

    if (error && calls.length === 0 && !search && activeFilterCount === 0) {
        return (
            <Box className={clsx(classes.card, classes.emptyCard)} mx="auto">
                <Typography style={{ marginBottom: 12 }}>
                    Unable to load call history.
                </Typography>
                <Button
                    variant="contained"
                    color="primary"
                    className={classes.btnPrimary}
                    onClick={() => refetch()}
                >
                    Retry
                </Button>
            </Box>
        )
    }

    if (
        calls.length === 0 &&
        !isLoading &&
        !search &&
        activeFilterCount === 0
    ) {
        return (
            <Box className={clsx(classes.card, classes.emptyCard)} mx="auto">
                <Box className={classes.emptyIconWrap}>
                    <PhoneIcon />
                </Box>
                <Typography className={classes.title}>No calls yet</Typography>
                <Typography color="textSecondary" style={{ marginTop: 8 }}>
                    Completed calls will show up here with a summary,
                    transcript, and recording.
                </Typography>
            </Box>
        )
    }

    return (
        <Box className={selectedCallId ? phone.callsGrid : undefined}>
            <Box className={`${phone.card} ${phone.cardNoPadding}`}>
                {/* Category tabs */}
                <Box
                    display="flex"
                    style={{
                        borderBottom: '1px solid #e5e7eb',
                        padding: '0 12px',
                    }}
                >
                    {CALL_CATEGORY_TABS.map((t) => (
                        <button
                            key={t.value}
                            type="button"
                            onClick={() => {
                                setCategoryTab(t.value)
                                setPage(1)
                            }}
                            style={{
                                padding: '10px 14px',
                                fontSize: '0.8125rem',
                                fontWeight: categoryTab === t.value ? 600 : 400,
                                color:
                                    categoryTab === t.value
                                        ? '#2563eb'
                                        : '#6b7280',
                                background: 'none',
                                border: 'none',
                                borderBottom:
                                    categoryTab === t.value
                                        ? '2px solid #2563eb'
                                        : '2px solid transparent',
                                cursor: 'pointer',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {t.label}
                        </button>
                    ))}
                </Box>

                {/* Search + Filter button */}
                <Box
                    className={phone.callListHeader}
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                    }}
                >
                    <TextField
                        fullWidth
                        size="small"
                        variant="outlined"
                        placeholder="Search transcript or caller…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className={phone.searchField}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon
                                        style={{
                                            fontSize: 14,
                                            color: '#9ca3af',
                                        }}
                                    />
                                </InputAdornment>
                            ),
                        }}
                    />
                    <Button
                        size="small"
                        variant="outlined"
                        className={classes.btnGhost}
                        startIcon={<FilterListIcon style={{ fontSize: 14 }} />}
                        onClick={() => setDrawerOpen(true)}
                        style={{
                            flexShrink: 0,
                            ...(activeFilterCount > 0
                                ? { borderColor: '#2563eb', color: '#2563eb' }
                                : {}),
                        }}
                    >
                        Filters
                        {activeFilterCount > 0 && (
                            <Chip
                                size="small"
                                label={activeFilterCount}
                                style={{
                                    height: 18,
                                    fontSize: '0.6875rem',
                                    fontWeight: 700,
                                    marginLeft: 6,
                                    backgroundColor: '#2563eb',
                                    color: '#fff',
                                }}
                            />
                        )}
                    </Button>
                </Box>

                {/* Active filter chips */}
                {activeFilterCount > 0 && (
                    <Box className={classes.activeChips}>
                        {dateFrom && (
                            <Chip
                                label={`From: ${dateFrom}`}
                                size="small"
                                onDelete={() => setDateFrom('')}
                                className={classes.filterChip}
                            />
                        )}
                        {dateTo && (
                            <Chip
                                label={`To: ${dateTo}`}
                                size="small"
                                onDelete={() => setDateTo('')}
                                className={classes.filterChip}
                            />
                        )}
                        {hasTranscript && (
                            <Chip
                                label={`Transcript: ${
                                    hasTranscript === 'true' ? 'Yes' : 'No'
                                }`}
                                size="small"
                                onDelete={() => setHasTranscript('')}
                                className={classes.filterChip}
                            />
                        )}
                        {hasRecording && (
                            <Chip
                                label={`Recording: ${
                                    hasRecording === 'true' ? 'Yes' : 'No'
                                }`}
                                size="small"
                                onDelete={() => setHasRecording('')}
                                className={classes.filterChip}
                            />
                        )}
                        {minDuration && (
                            <Chip
                                label={`Min: ${minDuration}s`}
                                size="small"
                                onDelete={() => setMinDuration('')}
                                className={classes.filterChip}
                            />
                        )}
                        {maxDuration && (
                            <Chip
                                label={`Max: ${maxDuration}s`}
                                size="small"
                                onDelete={() => setMaxDuration('')}
                                className={classes.filterChip}
                            />
                        )}
                        {callTopic && (
                            <Chip
                                label={`Topic: ${callTopic}`}
                                size="small"
                                onDelete={() => setCallTopic('')}
                                className={classes.filterChip}
                            />
                        )}
                        <Chip
                            label="Clear all"
                            size="small"
                            onClick={clearFilters}
                            className={classes.filterChipClear}
                        />
                    </Box>
                )}

                {/* Call list */}
                <Box className={phone.callListScroll}>
                    {isLoading ? (
                        <Box display="flex" justifyContent="center" py={4}>
                            <CircularProgress size={24} />
                        </Box>
                    ) : calls.length === 0 ? (
                        <Box p={3}>
                            <Typography color="textSecondary" variant="body2">
                                No calls match your filters.
                            </Typography>
                        </Box>
                    ) : (
                        calls.map((call) => (
                            <button
                                key={call.id}
                                type="button"
                                onClick={() => onSelectCall(call.id)}
                                className={clsx(
                                    phone.callListItem,
                                    selectedCallId === call.id &&
                                        phone.callListItemSelected,
                                )}
                            >
                                <Box
                                    className={clsx(
                                        phone.callTypeIcon,
                                        phone.callTypeIn,
                                    )}
                                >
                                    <CallReceivedIcon
                                        style={{ fontSize: 14 }}
                                    />
                                </Box>
                                <Box className={phone.callListMeta}>
                                    <Box
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="space-between"
                                        style={{ gap: 8 }}
                                    >
                                        <Typography
                                            variant="body2"
                                            style={{
                                                fontWeight: 600,
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            {call.tenant_name ||
                                                formatE164(call.caller_number)}
                                        </Typography>
                                        <span className={phone.callListTime}>
                                            {formatRelativeTime(
                                                call.created_at,
                                            )}
                                        </span>
                                    </Box>
                                    {call.tenant_name ? (
                                        <div className={phone.callListIntent}>
                                            {[
                                                call.tenant_location,
                                                formatE164(call.caller_number),
                                            ]
                                                .filter(Boolean)
                                                .join(' · ')}
                                        </div>
                                    ) : null}
                                    <div className={phone.callListIntent}>
                                        {call.call_category ===
                                            'GENERAL_SOLICITATION' &&
                                        call.followup_reason
                                            ? call.followup_reason
                                            : call.summary || 'Inbound call'}
                                    </div>
                                    <div className={phone.callListFooter}>
                                        {(() => {
                                            const cat =
                                                CALL_CATEGORY_META[
                                                    call.call_category
                                                ] ?? CALL_CATEGORY_META['']
                                            return (
                                                <Chip
                                                    label={cat.label}
                                                    size="small"
                                                    style={{
                                                        height: 18,
                                                        fontSize: 11,
                                                        fontWeight: 500,
                                                        backgroundColor: cat.bg,
                                                        color: cat.color,
                                                    }}
                                                />
                                            )
                                        })()}
                                        {call.call_topic && (
                                            <Chip
                                                label={call.call_topic}
                                                size="small"
                                                style={{
                                                    height: 18,
                                                    fontSize: 11,
                                                    fontWeight: 500,
                                                    backgroundColor: '#f3f4f6',
                                                    color: '#374151',
                                                }}
                                            />
                                        )}
                                        {call.lead_id && (
                                            <OutcomeChip
                                                label="Lead created"
                                                tone="positive"
                                            />
                                        )}
                                        <span className={phone.aiBadge}>
                                            <StarsIcon
                                                style={{ fontSize: 10 }}
                                            />{' '}
                                            AI
                                        </span>
                                        {call.has_transcript && (
                                            <span
                                                className={phone.forwardedLabel}
                                            >
                                                Transcript
                                            </span>
                                        )}
                                    </div>
                                </Box>
                            </button>
                        ))
                    )}
                </Box>

                {/* Pagination */}
                <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    px={2}
                    py={1}
                >
                    <Typography variant="caption" color="textSecondary">
                        {count} call{count !== 1 ? 's' : ''}
                    </Typography>
                    <Box style={{ display: 'flex', gap: 6 }}>
                        <Button
                            size="small"
                            variant="outlined"
                            className={classes.btnSmall}
                            disabled={!hasPrevious}
                            onClick={() => setPage((p) => p - 1)}
                        >
                            Prev
                        </Button>
                        <Button
                            size="small"
                            variant="outlined"
                            className={classes.btnSmall}
                            disabled={!hasNext}
                            onClick={() => setPage((p) => p + 1)}
                        >
                            Next
                        </Button>
                    </Box>
                </Box>
            </Box>

            {selectedCallId && (
                <CallDetailPanel
                    callId={selectedCallId}
                    onClose={handleDeselect}
                />
            )}

            {/* Filter drawer */}
            <Drawer
                anchor="right"
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
            >
                <Box className={classes.filterDrawer}>
                    {/* Header */}
                    <Box className={classes.filterDrawerHeader}>
                        <Box
                            display="flex"
                            alignItems="center"
                            style={{ gap: 10, flex: 1 }}
                        >
                            <Box className={classes.filterDrawerIcon}>
                                <FilterListIcon
                                    style={{ fontSize: 18, color: '#2563eb' }}
                                />
                            </Box>
                            <Typography
                                variant="h6"
                                style={{ fontWeight: 700, fontSize: '1.1rem' }}
                            >
                                Filters
                            </Typography>
                            {activeFilterCount > 0 && (
                                <Chip
                                    label={activeFilterCount}
                                    size="small"
                                    style={{
                                        height: 20,
                                        fontSize: '0.6875rem',
                                        fontWeight: 700,
                                        backgroundColor: '#2563eb',
                                        color: '#fff',
                                    }}
                                />
                            )}
                        </Box>
                        <IconButton
                            size="small"
                            onClick={() => setDrawerOpen(false)}
                            style={{ backgroundColor: '#f3f4f6', padding: 6 }}
                        >
                            <CloseIcon style={{ fontSize: 16 }} />
                        </IconButton>
                    </Box>

                    {/* Body */}
                    <Box className={classes.filterDrawerBody}>
                        {/* Date range */}
                        <Box className={classes.filterDrawerSection}>
                            <Typography
                                className={classes.filterDrawerLabel}
                                style={{ marginBottom: 8 }}
                            >
                                Date range
                            </Typography>
                            <Box style={{ display: 'flex', gap: 8 }}>
                                <TextField
                                    label="From"
                                    type="date"
                                    size="small"
                                    variant="outlined"
                                    fullWidth
                                    value={dateFrom}
                                    onChange={(e) =>
                                        setDateFrom(e.target.value)
                                    }
                                    InputLabelProps={{ shrink: true }}
                                />
                                <TextField
                                    label="To"
                                    type="date"
                                    size="small"
                                    variant="outlined"
                                    fullWidth
                                    value={dateTo}
                                    onChange={(e) => setDateTo(e.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Box>
                        </Box>

                        {/* Transcript */}
                        <Box className={classes.filterDrawerSection}>
                            <Typography className={classes.filterDrawerLabel}>
                                Transcript
                            </Typography>
                            <RadioGroup
                                value={hasTranscript}
                                onChange={(e) =>
                                    setHasTranscript(e.target.value)
                                }
                            >
                                <FormControlLabel
                                    value=""
                                    control={
                                        <Radio size="small" color="primary" />
                                    }
                                    label="Any"
                                />
                                <FormControlLabel
                                    value="true"
                                    control={
                                        <Radio size="small" color="primary" />
                                    }
                                    label="Has transcript"
                                />
                                <FormControlLabel
                                    value="false"
                                    control={
                                        <Radio size="small" color="primary" />
                                    }
                                    label="No transcript"
                                />
                            </RadioGroup>
                        </Box>

                        {/* Recording */}
                        <Box className={classes.filterDrawerSection}>
                            <Typography className={classes.filterDrawerLabel}>
                                Recording
                            </Typography>
                            <RadioGroup
                                value={hasRecording}
                                onChange={(e) =>
                                    setHasRecording(e.target.value)
                                }
                            >
                                <FormControlLabel
                                    value=""
                                    control={
                                        <Radio size="small" color="primary" />
                                    }
                                    label="Any"
                                />
                                <FormControlLabel
                                    value="true"
                                    control={
                                        <Radio size="small" color="primary" />
                                    }
                                    label="Has recording"
                                />
                                <FormControlLabel
                                    value="false"
                                    control={
                                        <Radio size="small" color="primary" />
                                    }
                                    label="No recording"
                                />
                            </RadioGroup>
                        </Box>

                        {/* Duration */}
                        <Box className={classes.filterDrawerSection}>
                            <Typography
                                className={classes.filterDrawerLabel}
                                style={{ marginBottom: 8 }}
                            >
                                Duration (seconds)
                            </Typography>
                            <Box style={{ display: 'flex', gap: 8 }}>
                                <TextField
                                    label="Min"
                                    type="number"
                                    size="small"
                                    variant="outlined"
                                    fullWidth
                                    value={minDuration}
                                    onChange={(e) =>
                                        setMinDuration(e.target.value)
                                    }
                                    InputLabelProps={{ shrink: true }}
                                    inputProps={{ min: 0 }}
                                />
                                <TextField
                                    label="Max"
                                    type="number"
                                    size="small"
                                    variant="outlined"
                                    fullWidth
                                    value={maxDuration}
                                    onChange={(e) =>
                                        setMaxDuration(e.target.value)
                                    }
                                    InputLabelProps={{ shrink: true }}
                                    inputProps={{ min: 0 }}
                                />
                            </Box>
                        </Box>

                        {/* Call Topic */}
                        <Box className={classes.filterDrawerSection}>
                            <Typography
                                className={classes.filterDrawerLabel}
                                style={{ marginBottom: 8 }}
                            >
                                Call Topic
                            </Typography>
                            <TextField
                                size="small"
                                variant="outlined"
                                fullWidth
                                placeholder="e.g. Pet policy"
                                value={callTopic}
                                onChange={(e) => setCallTopic(e.target.value)}
                            />
                        </Box>
                    </Box>

                    {/* Footer */}
                    {activeFilterCount > 0 && (
                        <Box className={classes.filterDrawerFooter}>
                            <Button
                                fullWidth
                                variant="outlined"
                                className={classes.btnGhost}
                                onClick={() => {
                                    clearFilters()
                                    setDrawerOpen(false)
                                }}
                            >
                                Clear all filters
                            </Button>
                        </Box>
                    )}
                </Box>
            </Drawer>
        </Box>
    )
}
